<template>
    <div class="keen-vehicle-selector">
        <div v-if="hasSelection" class="container-fluid" style="padding: 0;">
            <div v-if="label" style="margin-bottom: .5rem;" tabindex="0" class="ui-select__label">
                <div class="ui-select__label-text">{{ label }}</div>
            </div>
            <div class="row align-items-center">
                <div class="col-3" v-if="showImages" style="padding-right: 0;">
                    <vehicle-image :instance="this.selected"></vehicle-image>
                </div>
                <div class="col">
                    <div class="vehicle-title truncate" style="font-weight: bold; font-size: 1rem; min-width: 100%;">{{ this.selected.title }}</div>
                    <div v-if="!hideDerivative" class="vehicle-derivative">{{ this.selected.vehicle.derivative }}</div>
                    <div class="d-inline-block" v-if="!hideRegistration"><span class="registration small">{{ this.selected.vehicle.registration ? this.selected.vehicle.registration.formatted_vrm : 'No Reg' }}</span></div>
                    <div class="d-inline-block ml-0" v-if="this.selected.life_cycle_status_label"><span class="badge badge-secondary" style="padding: 7px 10px;">{{ this.selected.life_cycle_status_label }}</span></div>
                    <div class="d-inline-block ml-1" v-if="this.selected.location"><span class="badge badge-secondary" style="padding: .27rem 10px; vertical-align: middle;"><i class="material-icons" style="vertical-align: middle; margin-right: .25rem; font-size: 1rem;">location_on</i>{{ this.selected.location.name }}</span></div>
                </div>
                <div class="col text-right" style="max-width: 85px;">
                    <ui-icon-button icon="launch" @click="showVehicle" color="primary" type="secondary" size="mini"></ui-icon-button>
                    <ui-icon-button v-if="!disabled" icon="highlight_off" @click="removeVehicle" color="red" type="secondary" size="mini"></ui-icon-button>
                </div>
            </div>
        </div>
        <div style="width: 100%; display: flex;" class="align-items-baseline" v-else>
            <ui-select
            disable-filter
            has-search
            :label="label"
            :placeholder="placeholder"
            search-placeholder="Start typing to search"
            :keys="{ label: 'search_description', value: 'id' }"

            :loading="isLoading"
            :no-results="noResults"
            :options="options"

            @query-change="doFind"
            @dropdown-open="$emit('dropdown-open')"
            @dropdown-close="$emit('dropdown-close')"
            @change="instanceChanged"
            v-model="selected"
            :disabled="disabled"
            :invalid="invalid"
            :error="error"
            :icon="icon"
            class="select-control"
        >
            <div slot="option" slot-scope="scope" style="width: 100%;" class="container-fluid">
                <vehicle-description
                    show-location
                    :link-enabled="false"
                    image-class="col-4"
                    :instance="scope.option"
                    :show-image="showImages"
                    show-status-badge
                ></vehicle-description>
            </div>
        </ui-select>
            <div v-if="allowAddition && !forceStockingStatusSelection" style="margin-top: auto;">
                <button class="btn btn-success btn-sm" @click="newVehicle" :disabled="disabled">
                    <i class="material-icons" style="vertical-align: middle;">add</i>
                </button>
            </div>
            <div v-else-if="allowAddition" style="margin-top: auto; margin-left: 1rem;">
                <split-button color="green" icon="add" :options="[{ label: 'Add Stock', value: 'stock'},{ label: 'Add Non-Stock', value: 'non-stock'}]" label="" type="hidden-dropdown" @selected="newVehicle" :disabled="disabled"></split-button>
            </div>
        </div>

        <add-vehicle-form
            v-if="allowAddition"
            :is-alien="forceAlienLookup || addNonStock"
            :should-show-confirmation="true" 
            :should-hide-confirmation-buttons="true"
            ref="vehicle" 
            @instance-created="handleInstanceCreated"
        ></add-vehicle-form>
    </div>
</template>

<style lang="scss">
.keen-vehicle-selector {
    display: flex;
    margin-bottom: 1rem;

    .select-control {
        flex:1;
        margin-bottom: 0;
    }

    .btn {
        margin-left: 1rem;
    }
    
    .ui-select__display-value {
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
</style>


<script>
    import AddVehicleForm from '../vehicles/AddVehicleForm'
    import { mapActions } from 'vuex'
    import { VehicleInstanceResource } from '../../../classes/Api/Vehicle'
    import VehicleImage from "../../elements/VehicleImage";
    import SplitButton from "../../elements/SplitButton.vue";
    
    export default {
        components: {SplitButton, AddVehicleForm, VehicleImage },
        props: {
            label: {
                type: String,
            },
            value: {
                type: Object,
            },
            icon: {
                type: String,
            },
            placeholder: {
                type: String,
                default: 'Search by VRM, VIN, Make or Model',
            },
            status: {
                type: Array,
                default() {
                    return null;
                }
            },
            disabled: {
                type: Boolean,
                default() {
                    return false;
                }
            },
            invalid: {
                type: Boolean,
                default: false
            },
            error: {
                type: String
            },
            allowAddition: {
                type: Boolean,
                default() {
                    return true;
                }
            },
            allowClear: {
                type: Boolean,
                default() {
                    return true;
                }
            },
            showImages: {
                type: Boolean,
                default() {
                    return true;
                }
            },
            hideDerivative: {
                type: Boolean,
                default: false
            },
            warnBeforeClearing: {
                type: Boolean,
                default: false
            },
            hideRegistration: {
                type: Boolean,
                default: false
            },
            forceAlienLookup: {
                type:Boolean,
                default: true,
            },
            additionalRelationsToLoad: {
                type: Array,
                default() {
                    return []
                }
            },
            forceStockingStatusSelection: {
                type:Boolean,
                default: false,
            }
        },
        data() {
            return {
                isLoading: false,
                noResults: true,
                options: [],
                selected: {},
                addNonStock: false,
                lastQuery: '',
            }
        },
        watch: {
            value(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.selected = newVal;
                }
            }
        },
        created() {
            if (this.value) {
                this.selected = this.value
            }
        },
        computed: {
            hasSelection() {
                return this.selected && this.selected.id
            }
        },
        methods: {
            ...mapActions({ search: 'contact/search' }),
            handleInstanceCreated(instance) {
                this.selected = instance;
                this.$emit('input', instance);
                this.$refs['vehicle'].close();
            },
            newVehicle(val = null) {
                if (!val || typeof val !== 'object' || typeof val === 'object' && val.value === 'stock') {
                    this.addNonStock = false
                } else if (typeof val === 'object' && val.value === 'non-stock') {
                    this.addNonStock = true
                }

                this.$nextTick(() => {
                    this.$refs['vehicle'].open();
                })
            },
            instanceChanged(val) {
                this.$emit('input', val);
            },
            removeVehicle() {
                if (!this.warnBeforeClearing || confirm('Are you sure you want to remove this vehicle?')) {
                    this.$emit('cleared')
                    this.$emit('input', {})
                    this.selected = {}
                }
            },
            showVehicle() {
                this.$root.openInNewTab('vehicles/' + this.selected.id + '/overview')
            },
            doFind(query) {
                if (this.lastQuery === query.toLowerCase()) {
                    return
                }

                this.lastQuery = query.toLowerCase()
                this.noResults = true;
                this.options = [];
                this.onQueryChange(query);
                this.isLoading = true;
            },
            onQueryChange: _.debounce(function(query) {
                VehicleInstanceResource.cancelCurrentRequest();

                if (query.length > 2) {
                    this.isLoading = true;

                    let relations = this.additionalRelationsToLoad;

                    if (-1 === relations.indexOf('images')) {
                        relations.push('images')
                    }

                    let cancelToken = VehicleInstanceResource.createCancelToken()

                    VehicleInstanceResource.search(query, this.status, { with: relations }, { cancelToken }).then(response => {
                        this.noResults = false;
                        this.isLoading = false;

                        if (response.data.length === 0) {
                            this.noResults = true;
                        }

                        this.options = response.data;
                    }).catch(e => { 
                        handleApiError(e); 
                        this.isLoading = false; 
                        this.noResults = true; 
                    });
                } else {
                    this.options = [];
                    this.noResults = true;
                    this.isLoading = false;
                }
            }, 250)
        }
    }
</script>